import { memo } from 'react';
import { KeyValue, MoneyInput, Panel } from '@/components/ui';
import { formatINR, formatShortDuration, toAmount } from '@/utils/format';
import styles from '@/styles/components/BillTotals/style.scss';

interface BillTotalsProps {
  bookingsTotal: number;
  /** Paused time on the bookings and its cost (already in bookingsTotal). */
  pausedMs: number;
  pauseAmount: number;
  condimentsTotal: number;
  subtotal: number;
  total: number;
  discount: string;
  onDiscountChange: (value: string) => void;
  /** What happy hour gives the bookings (from the server, by their times); 0 hides the tick. */
  happyHour: number;
  /** False for a condiments-only bill: no Table time line and no discount. */
  hasBookings: boolean;
}

/** Table time, condiments, subtotal, discount and total. Memoised on plain values: payment typing skips it. */
export const BillTotals = memo(function BillTotals({
  bookingsTotal,
  pausedMs,
  pauseAmount,
  condimentsTotal,
  subtotal,
  total,
  discount,
  onDiscountChange,
  happyHour,
  hasBookings,
}: BillTotalsProps) {
  // Happy hour only fills the Discount input, so the box is ticked while the input holds that amount
  // (typing another discount unticks it).
  const happyHourOn = happyHour > 0 && toAmount(discount) === happyHour;

  return (
    <Panel>
      {hasBookings && <KeyValue label="Table time" value={formatINR(bookingsTotal)} />}
      {/* Shown, not deducted: staff decide whether to give the pause as a discount. */}
      {hasBookings && pausedMs > 0 && (
        <div className={styles.pauseRow}>
          <span>Pause · {formatShortDuration(pausedMs)} (in table time)</span>
          <span className={styles.pauseAmount}>{formatINR(pauseAmount)}</span>
        </div>
      )}
      <KeyValue label="Condiments" value={formatINR(condimentsTotal)} />
      <KeyValue label="Subtotal" value={formatINR(subtotal)} />
      {/* Discounts come off table time only, so a condiments-only bill has none. */}
      {hasBookings && happyHour > 0 && (
        <label className={styles.happyHour}>
          <input type="checkbox" checked={happyHourOn} onChange={(e) => onDiscountChange(e.target.checked ? String(happyHour) : '')} />
          <span>Happy hours discount</span>
          <span className={styles.happyHourAmount}>{formatINR(happyHour)}</span>
        </label>
      )}
      {hasBookings && (
        <div className={styles.discountRow}>
          <label htmlFor="bill-discount" className={styles.discountLabel}>
            Discount
          </label>
          <MoneyInput id="bill-discount" size="md" value={discount} onChange={onDiscountChange} className={styles.discountInput} max={subtotal} />
        </div>
      )}
      <KeyValue label="Total" value={formatINR(total)} emphasis />
    </Panel>
  );
});
